import { useAuth } from "../../context/AuthContext";
import { dbUserAndHub } from "../../config/api";
import { encryptUserAndHubPayload } from "../../utils/encryption";
import { compareStorePasskey, getBizPasskeyHashFromSettings } from "../../utils/bizPasskeyHash";
import { deleteStoreAPI, restoreStoreAPI } from "./StoreUtils";
import HubSetupBanner from "../../components/HubSetupBanner";
import { Breadcrumb } from "../../components/Breadcrumb";

import axios from "axios";
import StorefrontPreview from "./StorefrontPreview";
import CreateStoreWithPreview from "./createStore";
import { cdnUrl, cdnUrlForStorageKey } from "../../config/mediaPaths";
import { FOOTER } from "../../config/zinihubConfig";
import {
  resolveCatalogEntitlement,
} from "../../services/subscriptionCatalog";
import { getHubPlanId } from "../../utils/subscriptionState";
import { SUBSCRIPTION_GATE_LABELS } from "../../labels/subscriptionGateLabels";
import { shouldShowFirstDraftResult } from "../../config/guidedJourneyConfig";

import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useGuidedJourney } from "../../components/GuidedJourney";
import {
  App as AntApp,
  Spin,
  Modal,
  Input,
} from "antd";

import HubHeader from "./components/HubHeader";
import HubFilters from "./components/HubFilters";
import GridView from "./components/GridView";
import ListView from "./components/ListView";

import FirstRunSetup from "./components/FirstRunSetup";
import QRModal from "./components/QRModal";

import styles from "./Stores.module.css";
import PageContainer from "../../components/PageContainer/PageContainer";

const Stores = () => {
  const { message, modal } = AntApp.useApp();
  const {
    token,
    decodeToken,
    setToken,
    subscriptionCatalog,
    seedActiveStoreCount,
  } = useAuth();
  const decodedToken = useMemo(() => decodeToken(token), [token, decodeToken]);

  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedHubId, setSelectedHubId] = useState("");
  const [statusTab, setStatusTab] = useState("live");
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("hubViewMode") || "grid");
  const [sortBy, setSortBy] = useState("recent");
  const [visibilityFilters, setVisibilityFilters] = useState([]);
  const [typeFilters, setTypeFilters] = useState([]);

  const [modalState, setModalState] = useState({ open: false, mode: "create", shop: null });
  const [passkeyModal, setPasskeyModal] = useState({ open: false, shop: null, action: null });
  const [passkeyInput, setPasskeyInput] = useState("");
  const passkeyInputRef = React.useRef(null);
  const [hubSetupVisible, setHubSetupVisible] = useState(false);
  const [qrModal, setQrModal] = useState({ open: false, url: "", name: "" });
  const navigate = useNavigate();
  const location = useLocation();
  const {
    completeBusinessJourney,
    isCreateBusinessStep,
    markCreateBusinessStarted,
    showFirstDraft,
  } = useGuidedJourney();

  const handleViewModeChange = useCallback((mode) => {
    setViewMode(mode);
    localStorage.setItem("hubViewMode", mode);
  }, []);

  const isStoreDeleted = useCallback((shop) => {
    return (shop.activeFlag || "").toString().toUpperCase() === "DELETED";
  }, []);

  const isStoreLive = useCallback((shop) => shop?.BizSettings?.bizLive === true, []);

  const hubProfiles = useMemo(() => {
    const profiles = [];
    const addProfile = (profile) => { if (profile) profiles.push(profile); };
    const myHubAdmin = decodedToken?.MyHubAdmin;
    if (Array.isArray(myHubAdmin)) {
      myHubAdmin.forEach((entry) => {
        const hubProfile = entry?.HubProfile;
        if (Array.isArray(hubProfile)) hubProfile.forEach(addProfile);
        else addProfile(hubProfile);
      });
    }
    if (Array.isArray(decodedToken?.StaffAssignments)) {
      decodedToken.StaffAssignments.forEach((assignment) => addProfile(assignment?.HubProfile));
    }
    const uniqueByPk = new Map();
    profiles.forEach((profile) => {
      const pk = profile?.PK;
      if (pk && !uniqueByPk.has(pk)) uniqueByPk.set(pk, profile);
    });
    return Array.from(uniqueByPk.values());
  }, [decodedToken]);

  useEffect(() => {
    if (!selectedHubId && hubProfiles.length > 0) {
      setSelectedHubId(hubProfiles[0]?.PK || "");
    }
  }, [hubProfiles, selectedHubId]);

  const selectedHubProfile = useMemo(
    () => hubProfiles.find((profile) => profile?.PK === selectedHubId) || null,
    [hubProfiles, selectedHubId]
  );
  const selectedHubDomain = selectedHubProfile?.Domain || "";
  const selectedHubRole = useMemo(
    () => (decodedToken?.MyHubAdmin || []).some(
      (entry) => entry?.HubProfile?.PK === selectedHubId
    ) ? "owner" : "staff",
    [decodedToken, selectedHubId]
  );

  const createBusinessEntitlement = useMemo(
    () => subscriptionCatalog && selectedHubProfile
      ? resolveCatalogEntitlement(
          subscriptionCatalog,
          getHubPlanId(selectedHubProfile),
          {
            moduleId: "store",
            limitId: "stores",
            roles: ["owner"],
          },
          selectedHubRole
        )
      : null,
    [selectedHubProfile, selectedHubRole, subscriptionCatalog]
  );
  const activeStoreCount = useMemo(
    () => shops.filter(
      (shop) => shop.hubId === selectedHubId && !isStoreDeleted(shop)
    ).length,
    [isStoreDeleted, selectedHubId, shops]
  );
  const createBusinessDisabled = !createBusinessEntitlement?.allowed ||
    (
      Number.isFinite(createBusinessEntitlement?.limit) &&
      activeStoreCount >= createBusinessEntitlement.limit
    );
  const createBusinessDisabledReason = useMemo(() => {
    if (selectedHubRole !== "owner") return SUBSCRIPTION_GATE_LABELS.ownerOnlyCreate;
    if (!createBusinessEntitlement) return SUBSCRIPTION_GATE_LABELS.checkingStoreLimit;
    if (!createBusinessEntitlement.allowed) {
      return SUBSCRIPTION_GATE_LABELS.navigationUnavailable;
    }
    if (
      Number.isFinite(createBusinessEntitlement.limit) &&
      activeStoreCount >= createBusinessEntitlement.limit
    ) {
      return SUBSCRIPTION_GATE_LABELS.storeLimitReached(
        createBusinessEntitlement.limit
      );
    }
    return "";
  }, [
    activeStoreCount,
    createBusinessEntitlement,
    selectedHubRole,
  ]);

  useEffect(() => {
    if (!selectedHubId || loading) return;
    seedActiveStoreCount(selectedHubId, activeStoreCount);
  }, [activeStoreCount, loading, seedActiveStoreCount, selectedHubId]);

  const fetchStoresAPI = useCallback(async () => {
    const response = await axios.post(
      dbUserAndHub,
      encryptUserAndHubPayload({
        operationType: "read",
        action: "fetchHubs",
        token,
      }),
      { headers: { "Content-Type": "application/json" } }
    );
    const adminHubs = response.data?.adminHubs || {};
    const staffHubs = response.data?.staffHubs || {};
    const normalizeHubKey = (hubKey) => (hubKey || "").replace(/^HubId:/, "");

    const transformStore = (store, hubKey) => {
      const BizSettings = store.BizSettings
        ? {
            bizPrivate: store.BizSettings.bizPrivate ?? false,
            BizPasskeyHash: store.BizSettings.BizPasskeyHash,
            bizOpenStatus: store.BizSettings.bizOpenStatus ?? true,
            bizLive: store.BizSettings.bizLive === true,
            flashEnabled: store.BizSettings.flashEnabled ?? false,
            flashMessage: store.BizSettings.flashMessage,
            specialDiscount: store.BizSettings.specialDiscount,
            deliveryOptions: Array.isArray(store.BizSettings.deliveryOptions)
              ? store.BizSettings.deliveryOptions
              : ["PICKUP"],
            paymentMethods: Array.isArray(store.BizSettings.paymentMethods)
              ? store.BizSettings.paymentMethods
              : ["SELF_MANAGED"],
            businessDocs: Array.isArray(store.BizSettings.businessDocs)
              ? store.BizSettings.businessDocs
              : [],
            serviceCoverageScope: store.BizSettings.serviceCoverageScope || "ANYWHERE",
            serviceCoverageAreas: Array.isArray(store.BizSettings.serviceCoverageAreas)
              ? store.BizSettings.serviceCoverageAreas
              : [],
            prebookingGroups: store.BizSettings.prebookingGroups || "[]",
            socialFootprints:
              store.BizSettings.socialFootprints &&
              typeof store.BizSettings.socialFootprints === "object" &&
              !Array.isArray(store.BizSettings.socialFootprints)
                ? store.BizSettings.socialFootprints
                : {},
          }
        : null;

      const mapItemImages = (item) =>
        (item.ItemImages || [])
          .map((img) => {
            if (typeof img === "string") return { imageKey: img };
            if (img && typeof img === "object") {
              return { imageKey: img.imageKey, originalName: img.originalName };
            }
            return null;
          })
          .filter(Boolean);

      return {
        bizId: store.BizId,
        hubId: normalizeHubKey(hubKey),
        name: store.BizName,
        category: store.BizCategory,
        productSegment: store.ProductSegment,
        serviceSegment: store.ServiceSegment,
        address: store.Address,
        locality: store.Locality,
        city: store.City,
        state: store.State,
        country: store.Country,
        zip: store.Zip,
        mapUrl: store.MapUrl,
        geocode: store.Geocode,
        phone: store.Phone,
        storeType: store.BizType,
        products: (store.products || []).map((item) => ({ ...item, ItemImages: mapItemImages(item) })),
        services: (store.services || []).map((item) => ({ ...item, ItemImages: mapItemImages(item) })),
        version: store.Version,
        createdAt: store.CreatedOn,
        updatedAt: store.LastUpdatedOn,
        BizSettings,
        bizPrivate: BizSettings?.bizPrivate ?? false,
        bizLive: BizSettings?.bizLive === true,
        paymentMethods: BizSettings?.paymentMethods || ["SELF_MANAGED"],
        deliveryOptions: BizSettings?.deliveryOptions || ["PICKUP"],
        socialFootprints: BizSettings?.socialFootprints || {},
        bizOpenStatus: BizSettings?.bizOpenStatus ?? true,
        flashEnabled: BizSettings?.flashEnabled ?? false,
        flashMessage: BizSettings?.flashMessage,
        specialDiscount: BizSettings?.specialDiscount,
        activeFlag: store.ActiveFlag,
        timeToLive: store.TTL,
        deletedAt: store.DeletedAt,
        bizImageKey: store.BizImageKey || store.bizImageKey || "",
      };
    };

    const adminHubLists = Object.entries(adminHubs).flatMap(([hubKey, stores]) =>
      (stores || []).map((store) => transformStore(store, hubKey))
    );
    const staffHubLists = Object.entries(staffHubs).flatMap(([hubKey, stores]) =>
      (stores || []).map((store) => transformStore(store, hubKey))
    );
    const allStores = [...adminHubLists, ...staffHubLists];
    setShops(allStores);
    return response.data;
  }, [token]);

  const fetchStoresAPIRef = useRef(fetchStoresAPI);
  fetchStoresAPIRef.current = fetchStoresAPI;

  const refreshStores = useCallback(
    async (showLoading = true) => {
      if (showLoading) setLoading(true);
      try {
        await fetchStoresAPI();
      } catch (error) {
        message.error("An error occurred while fetching stores.");
        console.error(error);
      } finally {
        if (showLoading) setLoading(false);
      }
    },
    [fetchStoresAPI, message]
  );

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        await fetchStoresAPIRef.current();
      } catch (error) {
        message.error("An error occurred while fetching stores.");
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token, message]);

  const handleDelete = useCallback((shopToDelete) => {
    modal.confirm({
      title: "Delete this business?",
      content: "This will move the business to trash.",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          const response = await deleteStoreAPI(shopToDelete, token);
          if (response?.data?.statusCode === 200 || response?.status === 200) {
            await refreshStores(false);
            message.success("Biz deleted successfully.");
          } else {
            message.error(response?.data?.message || "Failed to delete Biz.");
          }
        } catch (error) {
          console.error("Delete error:", error);
          message.error(error?.response?.data?.message || "Error deleting the Biz.");
        }
      },
    });
  }, [token, refreshStores, message, modal]);

  const handleDeleteProtected = useCallback((shop) => {
    handleProtectedAction((unlockedShop) => {
      handleDelete(unlockedShop);
    }, shop);
  }, [handleDelete]);

  const handleRestore = useCallback(async (shopToRestore) => {
    if (createBusinessDisabled) {
      message.warning(createBusinessDisabledReason);
      return;
    }
    try {
      const response = await restoreStoreAPI(shopToRestore, token);
      if (response?.data?.statusCode === 200 || response?.status === 200) {
        await refreshStores(false);
        message.success("Biz restored successfully.");
      } else {
        message.error(response?.data?.message || "Failed to restore Biz.");
      }
    } catch (error) {
      console.error("Restore error:", error);
      message.error(error?.response?.data?.message || "Error restoring the Biz.");
    }
  }, [
    createBusinessDisabled,
    createBusinessDisabledReason,
    token,
    refreshStores,
    message,
  ]);

  const verifyPrivateStoreUnlock = (shop, passkey) => {
    if (!shop) return null;
    const storedHash = getBizPasskeyHashFromSettings(shop.BizSettings);
    if (!storedHash) { message.error("This private store has no passkey hash on file."); return null; }
    if (!compareStorePasskey(passkey, storedHash)) { message.error("Invalid passkey."); return null; }
    return { ...shop, bizPasskey: "", confirmPasskey: "" };
  };

  const handleProtectedAction = (action, shop) => {
    if (!shop?.bizPrivate) { action(shop); return; }
    setPasskeyInput("");
    setPasskeyModal({ open: true, shop, action });
  };

  const handlePasskeyConfirm = () => {
    if (!passkeyInput) { message.warning("Please enter passkey."); return; }
    if (!passkeyModal.shop || !passkeyModal.action) { setPasskeyModal({ open: false, shop: null, action: null }); return; }
    const unlockedShop = verifyPrivateStoreUnlock(passkeyModal.shop, passkeyInput);
    if (!unlockedShop) return;
    const runAction = passkeyModal.action;
    setPasskeyModal({ open: false, shop: null, action: null });
    setPasskeyInput("");
    runAction(unlockedShop);
  };

  const handleHubSetupComplete = async (newToken) => {
    if (newToken) setToken(newToken);
    setHubSetupVisible(false);
    await refreshStores();
  };

  const handleAddNewBiz = useCallback(() => {
    if (createBusinessDisabled) {
      message.warning(createBusinessDisabledReason);
      return;
    }
    const businessesForSelectedHub = shops.filter((shop) => shop.hubId === selectedHubId);
    const guidedJourneyStarted = isCreateBusinessStep;
    if (guidedJourneyStarted) {
      markCreateBusinessStarted();
    }
    navigate("/biz/create", {
      state: {
        mode: "create",
        existingBizIds: businessesForSelectedHub.map((shop) => shop.bizId),
        selectedHubId,
        guidedJourneyStarted,
        guidedFirstBusiness: guidedJourneyStarted && businessesForSelectedHub.length === 0,
      },
    });
  }, [
    createBusinessDisabled,
    createBusinessDisabledReason,
    isCreateBusinessStep,
    markCreateBusinessStarted,
    message,
    shops,
    selectedHubId,
    navigate,
  ]);

  useEffect(() => {
    const result = location.state?.guidedJourneyResult;
    if (!result || loading) return;

    if (result.cancelled) {
      navigate(location.pathname, { replace: true, state: null });
      completeBusinessJourney();
      return;
    }

    const createdBusiness = shops.find(
      (shop) => shop.hubId === result.hubId && shop.bizId === result.bizId
    );
    if (!createdBusiness) return;

    navigate(location.pathname, { replace: true, state: null });
    if (shouldShowFirstDraftResult(result)) {
      setSelectedHubId(result.hubId);
      setStatusTab("draft");
      setSearchQuery("");
      setVisibilityFilters([]);
      setTypeFilters([]);
      showFirstDraft(result.bizId);
    } else {
      completeBusinessJourney();
    }
  }, [
    completeBusinessJourney,
    loading,
    location.pathname,
    location.state,
    navigate,
    shops,
    showFirstDraft,
  ]);

  const hasNoMyHubAdmin = useMemo(() => {
    const myHubAdmin = decodedToken?.MyHubAdmin;
    return !myHubAdmin || (Array.isArray(myHubAdmin) && myHubAdmin.length === 0);
  }, [decodedToken]);

  const shopsForSelectedHub = useMemo(
    () => shops.filter((shop) => (selectedHubId ? shop.hubId === selectedHubId : true)),
    [shops, selectedHubId]
  );

  const searchedAndFilteredShops = useMemo(() => {
    let result = shopsForSelectedHub;

    if (statusTab === "trash") result = result.filter((s) => isStoreDeleted(s));
    else if (statusTab === "draft") result = result.filter((s) => !isStoreDeleted(s) && !isStoreLive(s));
    else result = result.filter((s) => !isStoreDeleted(s) && isStoreLive(s));

    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter(
        (s) =>
          (s.name || "").toLowerCase().includes(q) ||
          (s.productSegment || "").toLowerCase().includes(q) ||
          (s.serviceSegment || "").toLowerCase().includes(q) ||
          (s.city || "").toLowerCase().includes(q)
      );
    }

    if (visibilityFilters.length === 1) {
      if (visibilityFilters[0] === "public") result = result.filter((s) => !s.bizPrivate);
      if (visibilityFilters[0] === "private") result = result.filter((s) => !!s.bizPrivate);
    }

    if (typeFilters.length === 1) {
      if (typeFilters[0] === "product") result = result.filter((s) => !!s.productSegment);
      if (typeFilters[0] === "service") result = result.filter((s) => !!s.serviceSegment);
    }

    result = [...result].sort((a, b) => {
      switch (sortBy) {
        case "name": return (a.name || "").localeCompare(b.name || "");
        case "created": return (b.createdAt || "").localeCompare(a.createdAt || "");
        case "recent":
        default: return (b.updatedAt || "").localeCompare(a.updatedAt || "");
      }
    });

    return result;
  }, [shopsForSelectedHub, statusTab, searchQuery, visibilityFilters, typeFilters, sortBy, isStoreDeleted, isStoreLive]);

  const hubEmptyState = useMemo(() => {
    const liveCount = shopsForSelectedHub.filter((s) => !isStoreDeleted(s) && isStoreLive(s)).length;
    const draftCount = shopsForSelectedHub.filter((s) => !isStoreDeleted(s) && !isStoreLive(s)).length;

    if (liveCount === 0 && draftCount === 0) {
      return statusTab === "trash" ? "trashEmpty" : "noShops";
    }
    if (statusTab === "live" && liveCount === 0 && draftCount > 0) {
      return "makeLive";
    }
    return "noResults";
  }, [isStoreDeleted, isStoreLive, shopsForSelectedHub, statusTab]);

  const handleManage = useCallback((shop) => {
    handleProtectedAction((decryptedShop) => {
      setModalState({ open: true, mode: "view", shop: decryptedShop });
    }, shop);
  }, []);

  const handleEdit = useCallback((shop) => {
    handleProtectedAction((decryptedShop) => {
      navigate(`/biz/edit/${decryptedShop.bizId}`, {
        state: { mode: "edit", shop: decryptedShop, privateEditUnlockedFromHub: true },
      });
    }, shop);
  }, [navigate]);

  const handlePreview = useCallback((shop) => {
    handleProtectedAction((decryptedShop) => {
      setModalState({ open: true, mode: "view", shop: decryptedShop });
    }, shop);
  }, []);

  const handleQRCode = useCallback((shop) => {
    const url = `https://${selectedHubDomain || shop.bizId}.${FOOTER.CUSTOMER}/${shop.bizId}`;
    setQrModal({ open: true, url, name: shop.name });
  }, [selectedHubDomain]);

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <PageContainer>
      <Breadcrumb items={[
        { label: 'My Hub' }
      ]} />
      <HubHeader
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        hubProfiles={hubProfiles}
        selectedHubId={selectedHubId}
        onHubChange={setSelectedHubId}
        viewMode={viewMode}
        onViewModeChange={handleViewModeChange}
        sortBy={sortBy}
        onSortChange={setSortBy}
        onCreateBusiness={handleAddNewBiz}
        createBusinessDisabled={createBusinessDisabled}
        createBusinessDisabledReason={createBusinessDisabledReason}
      />

      {hubProfiles.length > 0 ? (
        <div className={styles.contentArea}>
          <HubFilters
            statusTab={statusTab}
            onStatusChange={setStatusTab}
            visibilityFilters={visibilityFilters}
            onVisibilityChange={setVisibilityFilters}
            typeFilters={typeFilters}
            onTypeFilterChange={setTypeFilters}
            shops={shopsForSelectedHub}
          />

          {viewMode === "grid" ? (
            <GridView
              shops={searchedAndFilteredShops}
              selectedHubDomain={selectedHubDomain}
              isStoreDeleted={isStoreDeleted}
              onManage={handleManage}
              onEdit={handleEdit}
              onDelete={handleDeleteProtected}
              onRestore={handleRestore}
              onPreview={handlePreview}
              onQRCode={handleQRCode}
              emptyType={hubEmptyState}
              onCreateBusiness={handleAddNewBiz}
              createBusinessDisabled={createBusinessDisabled}
              createBusinessDisabledReason={createBusinessDisabledReason}
            />
          ) : (
            <ListView
              shops={searchedAndFilteredShops}
              isStoreDeleted={isStoreDeleted}
              onManage={handleManage}
              onEdit={handleEdit}
              onDelete={handleDeleteProtected}
              onRestore={handleRestore}
              onPreview={handlePreview}
              statusLabel={statusTab === "draft" ? "draft" : statusTab === "trash" ? "trashed" : undefined}
            />
          )}
        </div>
      ) : (
        hasNoMyHubAdmin && <FirstRunSetup onStart={() => setHubSetupVisible(true)} />
      )}

      <Modal
        title={modalState.mode === "view" ? "Store Details" : "Create Your Store"}
        open={modalState.open}
        onCancel={() => setModalState({ open: false, mode: "create", shop: null })}
        footer={null}
        destroyOnClose
        className={modalState.mode === "view" ? styles.previewModal : undefined}
        width="100%"
        centered={false}
        style={{ top: 0, paddingBottom: 0, maxWidth: "100vw", margin: 0 }}
        styles={{
          content: {
            margin: 0,
            maxWidth: "100vw",
            top: 0,
            paddingBottom: 0,
            ...(modalState.mode === "view"
              ? { height: "100vh", display: "flex", flexDirection: "column" }
              : {}),
          },
          body: {
            ...(modalState.mode === "view" ? { flex: 1, overflowY: "auto", padding: 16 } : {}),
          },
        }}
      >
        {modalState.mode === "view" ? (
          <StorefrontPreview
            watchedValues={modalState.shop}
            products={modalState.shop?.products || []}
            services={modalState.shop?.services || []}
            hubId={modalState.shop?.hubId}
            bizId={modalState.shop?.bizId}
            resolveImg={(img) => {
              if (!img?.imageKey) return null;
              const hubId = modalState.shop?.hubId;
              const bizId = modalState.shop?.bizId;
              if (img.imageKey && !String(img.imageKey).includes("/") && hubId && bizId) {
                return cdnUrl({ entity: "item", fileType: "media", hubId, bizId, fileName: img.imageKey });
              }
              return cdnUrlForStorageKey(img.imageKey);
            }}
          />
        ) : (
          <CreateStoreWithPreview initialValues={modalState.shop} />
        )}
      </Modal>

      <Modal
        title="Enter Passkey"
        open={passkeyModal.open}
        onOk={handlePasskeyConfirm}
        afterOpenChange={(open) => {
          if (open) requestAnimationFrame(() => passkeyInputRef.current?.focus?.());
        }}
        onCancel={() => setPasskeyModal({ open: false, shop: null, action: null })}
        okText="Unlock"
      >
        <Input.Password
          ref={passkeyInputRef}
          autoFocus
          value={passkeyInput}
          onChange={(e) => setPasskeyInput(e.target.value)}
          onPressEnter={handlePasskeyConfirm}
          placeholder="Enter passkey"
        />
      </Modal>

      <HubSetupBanner
        visible={hubSetupVisible}
        onComplete={handleHubSetupComplete}
        onCancel={() => setHubSetupVisible(false)}
      />

      <QRModal
        open={qrModal.open}
        url={qrModal.url}
        name={qrModal.name}
        onClose={() => setQrModal({ open: false, url: "", name: "" })}
      />
    </PageContainer>
  );
};

export default Stores;
